import type {
  Clip,
  CustomCaptionStyle,
  Overlay,
  OverlayFontFamily,
  Transcript,
  TranscriptSegment,
} from "../types";

type CaptionHighlightId = "pink" | "yellow" | "blue" | "green" | "purple" | "white" | "rainbow";

type CaptionPositionId = "top" | "center" | "bottom";
type TextHookFontId =
  | "anton"
  | "inter-bold"
  | "caveat"
  | "bebas-neue"
  | "poppins-bold"
  | "league-gothic";
type TextHookAnimation = "none" | "fade-in" | "slide-up" | "bounce" | "typewriter";
type BrollTransition = "cut" | "crossfade" | "slide-up" | "slide-down";

interface LegacyCaptionSegment {
  start: number;
  end: number;
  text: string;
}

interface LegacyTrimLayer {
  kind: "trim";
  id: string;
  startSec: number;
  endSec: number;
}

interface LegacyBrollLayer {
  kind: "broll";
  id: string;
  clipUrl: string;
  trim: { startSec: number; endSec: number };
  insertAt:
    | { type: "prepend" }
    | { type: "append" }
    | { type: "atTime"; sec: number; mode?: "cover" | "insert" };
  muted: boolean;
  transition: BrollTransition;
  opacity: number;
  pictureInPicture?: unknown;
}

interface LegacyCaptionLayer {
  kind: "caption";
  id: string;
  text: string;
  position: CaptionPositionId;
  highlightColor: CaptionHighlightId;
  highlightActiveWord: boolean;
  fontFamily: TextHookFontId;
  segments?: LegacyCaptionSegment[];
}

interface LegacyTextHookLayer {
  kind: "textHook";
  id: string;
  text: string;
  box: { x: number; y: number; w: number; h: number };
  style: {
    fontFamily: TextHookFontId;
    fontSizePx: number;
    color: string;
    bgColor: string | null;
    bgOpacity: number;
    outlineWidthPx: number;
    outlineColor: string;
  };
  timing: { startSec: number; endSec: number };
  rotation?: number;
  scale?: number;
  animation?: TextHookAnimation;
}

interface LegacyMusicLayer {
  kind: "music";
  id: string;
}

type LegacyLayer =
  | LegacyTrimLayer
  | LegacyBrollLayer
  | LegacyCaptionLayer
  | LegacyTextHookLayer
  | LegacyMusicLayer;

export interface LegacyEditorSnapshot {
  version?: number;
  layers?: unknown[];
}

export interface VideoAdSnapshotMapperInput {
  snapshot: unknown;
  baseVideoUrl: string;
  sourceStorageKey?: string | null;
  sourceMimeType?: string | null;
  baseDurationSec?: number | null;
  language?: string | null;
}

export interface VideoAdProjectPatch {
  clips?: Clip[];
  transcript?: Transcript;
  presetId?: string | null;
  customStyle?: CustomCaptionStyle | null;
  overlays?: Overlay[];
}

export interface UnsupportedVideoAdSnapshotField {
  layerId: string;
  kind: string;
  field: string;
  reason: string;
}

export interface VideoAdSnapshotMappingResult {
  patch: VideoAdProjectPatch;
  unsupported: UnsupportedVideoAdSnapshotField[];
}

const FONT_MAP: Record<TextHookFontId, OverlayFontFamily> = {
  anton: "Anton",
  "inter-bold": "Inter",
  caveat: "Caveat",
  "bebas-neue": "Bebas Neue",
  "poppins-bold": "Poppins",
  "league-gothic": "Bebas Neue",
};

const HIGHLIGHT_COLORS: Record<CaptionHighlightId, string> = {
  pink: "#FF4FD8",
  yellow: "#FFD400",
  blue: "#3BA7FF",
  green: "#36D399",
  purple: "#A855F7",
  white: "#FFFFFF",
  rainbow: "#FFD400",
};

const CAPTION_POSITION_Y: Record<CaptionPositionId, number> = {
  top: 0.22,
  center: 0.5,
  bottom: 0.86,
};

const CLIP_EPS = 1e-6;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function finiteNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

function positiveRange(start: number, end: number): { start: number; end: number } | null {
  const s = Math.max(0, start);
  const e = Math.max(0, end);
  return e > s + CLIP_EPS ? { start: s, end: e } : null;
}

function idFor(prefix: string, id: unknown, index: number): string {
  const raw = typeof id === "string" && id.trim() ? id.trim() : `${prefix}-${index + 1}`;
  return `${prefix}-${raw.replace(/[^a-z0-9_-]+/gi, "-")}`.slice(0, 64);
}

function rgbaWithOpacity(color: string | null, opacity: number): string | undefined {
  if (!color) return undefined;
  const alpha = clamp(opacity, 0, 1);
  if (/^#[0-9a-f]{6}$/i.test(color)) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
}

function isLegacyLayer(value: unknown): value is LegacyLayer {
  const record = asRecord(value);
  const kind = record?.kind;
  return (
    kind === "trim" ||
    kind === "broll" ||
    kind === "caption" ||
    kind === "textHook" ||
    kind === "music"
  );
}

function legacyLayers(snapshot: unknown): LegacyLayer[] {
  const record = asRecord(snapshot);
  const layers = record?.layers;
  return Array.isArray(layers) ? layers.filter(isLegacyLayer) : [];
}

function makeSourceClip(
  id: string,
  input: VideoAdSnapshotMapperInput,
  start: number,
  end: number,
): Clip | null {
  const range = positiveRange(start, end);
  if (!range) return null;
  return {
    id,
    kind: "source",
    url: input.baseVideoUrl,
    storageKey: input.sourceStorageKey ?? "",
    mimeType: input.sourceMimeType ?? "video/mp4",
    trimStartSec: range.start,
    trimEndSec: range.end,
    muted: false,
  };
}

function brollDuration(layer: LegacyBrollLayer): number {
  return Math.max(0, layer.trim.endSec - layer.trim.startSec);
}

function withTransition(clip: Clip, transition: BrollTransition): Clip {
  if (transition === "cut") return clip;
  const transitionInKind: Clip["transitionInKind"] =
    transition === "crossfade" ? "fade" : transition;
  return {
    ...clip,
    transitionInSec: 0.3,
    transitionInKind,
  };
}

function makeBrollClip(layer: LegacyBrollLayer, index: number): Clip | null {
  const range = positiveRange(layer.trim.startSec, layer.trim.endSec);
  if (!range || !layer.clipUrl) return null;
  const clip: Clip = {
    id: idFor("broll", layer.id, index),
    kind: "broll",
    url: layer.clipUrl,
    storageKey: "",
    mimeType: "video/mp4",
    trimStartSec: range.start,
    trimEndSec: range.end,
    muted: Boolean(layer.muted),
    assetDurationSec: Math.max(range.end, brollDuration(layer)),
    ...(layer.muted ? { sourceAudioMode: "continue", sourceAudioVolume: 1 } : {}),
  };
  return withTransition(clip, layer.transition);
}

function clipDuration(clip: Clip): number {
  return Math.max(0, clip.trimEndSec - clip.trimStartSec);
}

function insertAtTimeline(
  clips: Clip[],
  atSec: number,
  insert: Clip,
  mode: "cover" | "insert",
): Clip[] {
  const target = Math.max(0, atSec);
  const insertDuration = clipDuration(insert);
  let offset = 0;

  for (let index = 0; index < clips.length; index += 1) {
    const clip = clips[index];
    const duration = clipDuration(clip);
    const nextOffset = offset + duration;
    if (target <= offset + CLIP_EPS) {
      return [...clips.slice(0, index), insert, ...clips.slice(index)];
    }
    if (target < nextOffset - CLIP_EPS) {
      if (clip.kind !== "source") {
        return [...clips.slice(0, index + 1), insert, ...clips.slice(index + 1)];
      }
      const beforeDuration = target - offset;
      const afterStart =
        clip.trimStartSec + beforeDuration + (mode === "cover" ? insertDuration : 0);
      const before = makeSourceClip(
        `${clip.id}-a`,
        {
          snapshot: null,
          baseVideoUrl: clip.url,
          sourceStorageKey: clip.storageKey,
          sourceMimeType: clip.mimeType,
        },
        clip.trimStartSec,
        clip.trimStartSec + beforeDuration,
      );
      const after = makeSourceClip(
        `${clip.id}-b`,
        {
          snapshot: null,
          baseVideoUrl: clip.url,
          sourceStorageKey: clip.storageKey,
          sourceMimeType: clip.mimeType,
        },
        afterStart,
        clip.trimEndSec,
      );
      return [
        ...clips.slice(0, index),
        ...(before ? [before] : []),
        insert,
        ...(after ? [after] : []),
        ...clips.slice(index + 1),
      ];
    }
    offset = nextOffset;
  }

  return [...clips, insert];
}

function mapClips(
  layers: LegacyLayer[],
  input: VideoAdSnapshotMapperInput,
  unsupported: UnsupportedVideoAdSnapshotField[],
): Clip[] | undefined {
  const trim = layers.find((layer): layer is LegacyTrimLayer => layer.kind === "trim");
  const brolls = layers.filter((layer): layer is LegacyBrollLayer => layer.kind === "broll");
  const needsClips = Boolean(trim) || brolls.length > 0;
  if (!needsClips) return undefined;

  const duration = input.baseDurationSec != null ? Math.max(0, input.baseDurationSec) : null;
  const sourceStart = trim ? finiteNumber(trim.startSec, 0) : 0;
  const sourceEnd = trim ? finiteNumber(trim.endSec, duration ?? 0) : duration;
  if (sourceEnd == null || sourceEnd <= sourceStart) {
    for (const layer of brolls) {
      unsupported.push({
        layerId: layer.id,
        kind: layer.kind,
        field: "insertAt",
        reason: "Cannot migrate b-roll without a known source duration.",
      });
    }
    return undefined;
  }

  const sourceClip = makeSourceClip("source-legacy", input, sourceStart, sourceEnd);
  if (!sourceClip) return undefined;
  let clips: Clip[] = [sourceClip];
  const prepended: Clip[] = [];
  const appended: Clip[] = [];

  brolls.forEach((layer, index) => {
    const clip = makeBrollClip(layer, index);
    if (!clip) return;
    if (layer.opacity !== 1) {
      unsupported.push({
        layerId: layer.id,
        kind: layer.kind,
        field: "opacity",
        reason: "Canonical timeline clips do not model per-b-roll opacity yet.",
      });
    }
    if (layer.pictureInPicture) {
      unsupported.push({
        layerId: layer.id,
        kind: layer.kind,
        field: "pictureInPicture",
        reason: "PiP preview fields are not rendered by the unified timeline yet.",
      });
    }

    if (layer.insertAt.type === "prepend") {
      prepended.push(clip);
      return;
    }
    if (layer.insertAt.type === "append") {
      appended.push(clip);
      return;
    }
    clips = insertAtTimeline(
      clips,
      Math.max(0, layer.insertAt.sec - sourceStart),
      clip,
      layer.insertAt.mode ?? "cover",
    );
  });

  return [...prepended, ...clips, ...appended];
}

function normalizeCaptionSegments(
  segments: readonly LegacyCaptionSegment[] | undefined,
  fallbackText: string,
  durationSec: number | null | undefined,
): TranscriptSegment[] {
  const normalized = (segments ?? [])
    .map((segment) => {
      const range = positiveRange(segment.start, segment.end);
      if (!range || !segment.text.trim()) return null;
      return { start: range.start, end: range.end, text: segment.text.trim() };
    })
    .filter((segment): segment is TranscriptSegment => segment !== null);
  if (normalized.length > 0) return normalized;
  if (!fallbackText.trim() || !durationSec || durationSec <= 0) return [];
  return [{ start: 0, end: durationSec, text: fallbackText.trim() }];
}

function mapCaption(
  layers: LegacyLayer[],
  input: VideoAdSnapshotMapperInput,
): Pick<VideoAdProjectPatch, "transcript" | "presetId" | "customStyle"> {
  const caption = layers.find((layer): layer is LegacyCaptionLayer => layer.kind === "caption");
  if (!caption) return {};
  const segments = normalizeCaptionSegments(caption.segments, caption.text, input.baseDurationSec);
  return {
    presetId: caption.highlightActiveWord ? "word-by-word-accent" : "caption-lite",
    customStyle: {
      positionY: CAPTION_POSITION_Y[caption.position] ?? 0.86,
      color: HIGHLIGHT_COLORS[caption.highlightColor] ?? "#FFFFFF",
      fontFamily: FONT_MAP[caption.fontFamily] ?? "Inter",
      fontWeight: caption.highlightActiveWord ? 800 : 700,
    },
    ...(segments.length > 0
      ? {
          transcript: {
            language: input.language ?? "en",
            segments,
            rawProvider: "legacy-video-ad-editor",
          },
        }
      : {}),
  };
}

function mapAnimation(animation: TextHookAnimation | undefined): Overlay["animIn"] {
  if (animation === "slide-up") return "slide-up";
  if (animation === "bounce") return "pop";
  if (animation === "fade-in") return "fade";
  return "none";
}

function mapOverlays(
  layers: LegacyLayer[],
  unsupported: UnsupportedVideoAdSnapshotField[],
): Overlay[] | undefined {
  const hooks = layers.filter((layer): layer is LegacyTextHookLayer => layer.kind === "textHook");
  if (hooks.length === 0) return undefined;
  return hooks.map((hook, index) => {
    if (hook.scale != null && Math.abs(hook.scale - 1) > 0.01) {
      unsupported.push({
        layerId: hook.id,
        kind: hook.kind,
        field: "scale",
        reason: "Overlay scale is represented by box dimensions in the unified editor.",
      });
    }
    if (hook.animation === "typewriter") {
      unsupported.push({
        layerId: hook.id,
        kind: hook.kind,
        field: "animation",
        reason: "Typewriter hook animation downgrades to a static text overlay.",
      });
    }
    const fontSize = clamp(hook.style.fontSizePx / 1000, 0.02, 0.2);
    return {
      id: idFor("overlay", hook.id, index),
      kind: hook.style.bgColor ? "pill-hook" : "text-box",
      role: "legacy-video-ad-hook",
      lane: "legacy-video-ad-hooks",
      x: clamp(hook.box.x, 0, 1),
      y: clamp(hook.box.y, 0, 1),
      w: clamp(hook.box.w, 0.02, 1),
      h: clamp(hook.box.h, 0.02, 1),
      anchor: "tl",
      startSec: Math.max(0, hook.timing.startSec),
      endSec: Math.max(hook.timing.startSec + 0.1, hook.timing.endSec),
      text: hook.text,
      style: {
        color: hook.style.color,
        backgroundColor: rgbaWithOpacity(hook.style.bgColor, hook.style.bgOpacity),
        fontSize,
        fontFamily: FONT_MAP[hook.style.fontFamily] ?? "Inter",
        fontWeight: 900,
        textAlign: "center",
        borderWidth: hook.style.outlineWidthPx,
        borderColor: hook.style.outlineColor,
        rotation: hook.rotation ?? 0,
      },
      animIn: mapAnimation(hook.animation),
      animOut: "fade",
      source: { kind: "manual" },
    };
  });
}

export function mapEditorSnapshotToVideoEditSpecDraft(
  input: VideoAdSnapshotMapperInput,
): VideoAdSnapshotMappingResult {
  const layers = legacyLayers(input.snapshot);
  const unsupported: UnsupportedVideoAdSnapshotField[] = [];
  for (const layer of layers) {
    if (layer.kind === "music") {
      unsupported.push({
        layerId: layer.id,
        kind: layer.kind,
        field: "music",
        reason: "Background music needs the planned unified audio slice before migration.",
      });
    }
  }

  const clips = mapClips(layers, input, unsupported);
  const overlays = mapOverlays(layers, unsupported);
  const captionPatch = mapCaption(layers, input);

  return {
    patch: {
      ...(clips ? { clips } : {}),
      ...(overlays ? { overlays } : {}),
      ...captionPatch,
    },
    unsupported,
  };
}
